'use client';

import {uniq} from 'lodash';
import {Suspense, lazy, useMemo} from 'react';
import type {VisitedStats} from 'server/apiHandlers/getVisitedStatsHandler';
import VisitedPlacesMapSideContent from './VisitedPlacesMapSideContent';
import useMapSelectedPlacesList from './useMapSelectedPlacesList';
import type {MyList} from 'server/apiHandlers/getMyListsHandler';
import useMapUtils, {useMapResize} from './useMapUtils';
import useSearchParam from 'src/hooks/useSearchParam';
import {useFilteredPlaces} from './useFilteredPlaces';
import {addLine} from 'src/utils/icons';
import BaseButton from '../BaseButton';
import RemixIcon from '../RemixIcon';
import MapLayout from './MapLayout';
import useMap from './useMap';

const AddVisitedPlacesModal = lazy(() => import('./AddVisitedPlacesModal'));

let wasFetching = false;

type Props = {
  list: HumboList;
  reviews: Review[];
  myLists: MyList[];
  visitedStats: VisitedStats;
  session: IronSession | null;
  bannerComponent?: React.ReactNode;
};

const VisitedPlacesMapContent = ({list, reviews, myLists, visitedStats, session, bannerComponent}: Props) => {
  const [activeTab] = useSearchParam('tab', 'countries');
  const [placesListQuery] = useSearchParam('places-list');

  const countryCodes = useMemo(() => uniq(list.places.map((place) => place.place.countryCode)), [list.places]);

  const variant = placesListQuery ? 'places' : (activeTab as 'countries' | 'places');

  const {placesList, isFetching} = useMapSelectedPlacesList();

  const places: Place[] = useMemo(
    () => list.places.map((placeEntry) => ({...placeEntry.place, dateAdded: placeEntry.dateAdded})),
    [list]
  );

  const filteredPlaces = useFilteredPlaces(places);

  const {mapContainer, map} = useMap({
    variant,
    highlightedCountryCodes: countryCodes,
    places: placesListQuery
      ? placesList?.places.map((placeEntry) => placeEntry.place).filter((place: Place) => place?.location) || []
      : filteredPlaces
  });

  const {zoomToPlaces} = useMapUtils(map);

  if (wasFetching && !isFetching && placesList?.places.length) {
    zoomToPlaces(placesList?.places.map((placeEntry) => placeEntry.place));
  }

  wasFetching = isFetching;

  useMapResize(mapContainer, map);

  const isMyList = session?._id === list.user?._id;
  const addedManyPlaces = list.places.length >= 10;

  const AddListButton = isMyList ? (
    <BaseButton buttonVariant={addedManyPlaces ? 'text' : 'primary'} size="small">
      <RemixIcon
        icon={addLine}
        size={20}
        color={addedManyPlaces ? 'var(--text-color)' : 'white'}
        className="-ml-[6px] mr-1 -mt-px"
      />{' '}
      Add Places
    </BaseButton>
  ) : null;

  return (
    <MapLayout
      map={map}
      session={session}
      places={places}
      reviews={reviews}
      myLists={myLists}
      mapContainer={mapContainer}
      headerComponent={
        isMyList ? (
          <Suspense fallback={AddListButton}>
            <AddVisitedPlacesModal>{AddListButton}</AddVisitedPlacesModal>
          </Suspense>
        ) : null
      }
      listName={list.name}
      listUser={list.user}
    >
      <VisitedPlacesMapSideContent
        list={list}
        places={places}
        reviews={reviews}
        visitedStats={visitedStats}
        map={map}
      />

      {bannerComponent}
    </MapLayout>
  );
};

export default VisitedPlacesMapContent;
